import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import type { MainView } from '../../../core/domain/types';
import { useGameStore } from '../../../store/gameStore';
import { useUiStore } from '../../../store/uiStore';
import { currentTutorialStep, TUTORIAL_STEPS } from '../domain/tutorialRules';

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

type TargetStatus = 'locating' | 'found' | 'missing';

const viewNames: Record<MainView, string> = {
  farm: '农场',
  home: '小屋',
  wardrobe: '换装',
  shop: '商店',
  quests: '任务',
};

function visibleRect(element: HTMLElement): TargetRect | null {
  const rect = element.getBoundingClientRect();
  if (rect.width < 1 || rect.height < 1) return null;
  return { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
}

export function TutorialOverlay() {
  const tutorial = useGameStore((state) => state.game.tutorial);
  const actions = useGameStore((state) => state.actions);
  const view = useUiStore((state) => state.view);
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const [targetStatus, setTargetStatus] = useState<TargetStatus>('locating');
  const step = currentTutorialStep(tutorial);
  const targetId = useMemo(
    () => (step && view === step.view ? step.targetId : step ? `nav-${step.view}` : ''),
    [step, view],
  );

  useEffect(() => {
    if (!step || !targetId) return;
    let cancelled = false;
    let timeoutId: number | undefined;
    let frameId: number | undefined;
    let attempts = 0;
    let target: HTMLElement | null = null;

    const updateRect = () => {
      if (!target || !document.contains(target)) return;
      const rect = visibleRect(target);
      if (rect) setTargetRect(rect);
    };

    const locate = () => {
      if (cancelled) return;
      target = document.querySelector<HTMLElement>(`[data-tutorial-target="${targetId}"]`);
      const rect = target ? visibleRect(target) : null;
      if (target && rect) {
        setTargetRect(rect);
        setTargetStatus('found');
        if (!targetId.startsWith('nav-')) {
          target.scrollIntoView({ block: 'center', inline: 'nearest' });
          frameId = window.requestAnimationFrame(updateRect);
        }
        return;
      }
      attempts += 1;
      if (attempts >= 20) {
        setTargetRect(null);
        setTargetStatus('missing');
        return;
      }
      timeoutId = window.setTimeout(locate, 100);
    };

    frameId = window.requestAnimationFrame(() => {
      setTargetRect(null);
      setTargetStatus('locating');
      locate();
    });
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      if (frameId !== undefined) window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [step, targetId]);

  if (!step) return null;
  const stepNumber = tutorial.step + 1;
  const onExpectedView = view === step.view;

  return (
    <>
      {targetRect && targetStatus === 'found' && (
        <div
          aria-hidden="true"
          className="tutorial-spotlight"
          style={
            {
              '--tutorial-top': `${targetRect.top - 6}px`,
              '--tutorial-left': `${targetRect.left - 6}px`,
              '--tutorial-width': `${targetRect.width + 12}px`,
              '--tutorial-height': `${targetRect.height + 12}px`,
            } as CSSProperties
          }
        />
      )}
      <aside
        className="tutorial-card"
        role="status"
        aria-live="polite"
        data-tutorial-step={step.id}
      >
        <div className="tutorial-copy">
          <span className="tutorial-progress">
            第 {stepNumber}/{TUTORIAL_STEPS.length} 步
          </span>
          <strong>{step.title}</strong>
          <p>
            {onExpectedView
              ? step.message
              : `请先点击底部“${viewNames[step.view]}”。${step.message}`}
          </p>
          {targetStatus === 'locating' && <small>正在定位操作位置……</small>}
          {targetStatus === 'missing' && (
            <small className="tutorial-warning">
              当前页面暂时找不到操作位置。你可以继续自由操作，或跳过这一步。
            </small>
          )}
        </div>
        <div className="tutorial-actions">
          {targetStatus === 'missing' && (
            <button className="primary-button" onClick={actions.skipTutorialStep}>
              跳过本步
            </button>
          )}
          <button onClick={actions.skipTutorial}>跳过全部</button>
        </div>
      </aside>
    </>
  );
}
